/**
 * Chemical Equilibrium & Le Chatelier's Principle drill bank.
 *
 * Categories:
 *  - concept: dynamic equilibrium, reversibility
 *  - keq: writing & interpreting K expressions, Kc vs Kp, Q vs K
 *  - lechat-conc: concentration stresses
 *  - lechat-pressure: pressure / volume stresses
 *  - lechat-temp: temperature stresses (endo/exo)
 *  - lechat-other: catalyst, inert gas, common ion
 */

export type EqCategory =
  | "concept"
  | "keq"
  | "lechat-conc"
  | "lechat-pressure"
  | "lechat-temp"
  | "lechat-other";

export type EqDifficulty = "easy" | "medium" | "hard";

export const EQ_CATEGORY_LABELS: Record<EqCategory, string> = {
  concept: "Equilibrium Concept",
  keq: "K Expressions & Q",
  "lechat-conc": "Le Chât: Concentration",
  "lechat-pressure": "Le Chât: Pressure",
  "lechat-temp": "Le Chât: Temperature",
  "lechat-other": "Catalyst & Other",
};

export interface EqProblem {
  id: string;
  category: EqCategory;
  difficulty: EqDifficulty;
  prompt: string;
  /** Optional reaction or expression to render in monospace. */
  formula?: string;
  options: string[];
  correct: number;
  insight: string;
}

export const EQ_PROBLEMS: EqProblem[] = [
  // ── concept ────────────────────────────────────────────────────────────
  {
    id: "con-1",
    category: "concept",
    difficulty: "easy",
    prompt: "At dynamic equilibrium, which statement is TRUE?",
    options: [
      "Forward and reverse reactions have stopped.",
      "Forward and reverse rates are equal; concentrations are constant.",
      "Concentrations of reactants and products are equal.",
      "Only the forward reaction is occurring.",
    ],
    correct: 1,
    insight:
      "Dynamic ≠ static. Both reactions still happen, but at equal rates, so concentrations stop changing — they are usually NOT equal to each other.",
  },
  {
    id: "con-2",
    category: "concept",
    difficulty: "easy",
    prompt: "Equilibrium can only be reached in a:",
    options: [
      "Open system",
      "Closed system",
      "System at 0 K",
      "Vacuum",
    ],
    correct: 1,
    insight:
      "If reactants or products can escape (open system), the reverse reaction is starved and equilibrium never establishes.",
  },
  {
    id: "con-3",
    category: "concept",
    difficulty: "medium",
    prompt:
      "If K = 1.0 × 10⁻⁵ at a given temperature, the equilibrium mixture mostly contains:",
    options: [
      "Mostly products",
      "Roughly equal reactants and products",
      "Mostly reactants",
      "Cannot be determined without Q",
    ],
    correct: 2,
    insight:
      "K << 1 means [products] is small relative to [reactants] — equilibrium lies far to the LEFT.",
  },

  // ── K expression / Q ───────────────────────────────────────────────────
  {
    id: "keq-1",
    category: "keq",
    difficulty: "easy",
    prompt: "Write Kc for: N₂(g) + 3 H₂(g) ⇌ 2 NH₃(g)",
    formula: "N₂(g) + 3 H₂(g) ⇌ 2 NH₃(g)",
    options: [
      "Kc = [N₂][H₂]³ / [NH₃]²",
      "Kc = [NH₃]² / ([N₂][H₂]³)",
      "Kc = [NH₃] / ([N₂][H₂])",
      "Kc = [NH₃]² / ([N₂] + 3[H₂])",
    ],
    correct: 1,
    insight:
      "Products over reactants, each raised to its stoichiometric coefficient.",
  },
  {
    id: "keq-2",
    category: "keq",
    difficulty: "medium",
    prompt:
      "For CaCO₃(s) ⇌ CaO(s) + CO₂(g), the correct equilibrium expression is:",
    formula: "CaCO₃(s) ⇌ CaO(s) + CO₂(g)",
    options: [
      "Kc = [CaO][CO₂] / [CaCO₃]",
      "Kc = [CO₂]",
      "Kc = [CaCO₃] / ([CaO][CO₂])",
      "Kc = 1 / [CO₂]",
    ],
    correct: 1,
    insight:
      "Pure solids and pure liquids are omitted (their 'concentrations' are constant). Only gases and aqueous species appear in K.",
  },
  {
    id: "keq-3",
    category: "keq",
    difficulty: "medium",
    prompt:
      "If Q < K for a reaction at a given moment, the system will:",
    options: [
      "Shift LEFT (reverse) to make more reactants.",
      "Shift RIGHT (forward) to make more products.",
      "Already be at equilibrium.",
      "Have a negative ΔG of zero magnitude.",
    ],
    correct: 1,
    insight:
      "Q < K means there are too few products relative to equilibrium. The forward reaction proceeds to push Q up toward K.",
  },
  {
    id: "keq-4",
    category: "keq",
    difficulty: "hard",
    prompt:
      "K depends on which of the following?",
    options: [
      "Initial concentrations of reactants",
      "Presence of a catalyst",
      "Temperature only",
      "Total pressure of the system",
    ],
    correct: 2,
    insight:
      "K is a function of T alone. Catalysts, initial amounts, and pressure changes do not change K — they may change the position of equilibrium without changing the constant.",
  },

  // ── Le Chât: concentration ─────────────────────────────────────────────
  {
    id: "lc-1",
    category: "lechat-conc",
    difficulty: "easy",
    prompt:
      "For N₂ + 3 H₂ ⇌ 2 NH₃, what happens if you ADD more N₂?",
    formula: "N₂(g) + 3 H₂(g) ⇌ 2 NH₃(g)",
    options: [
      "Shifts left, more N₂ + H₂ form",
      "Shifts right, more NH₃ forms",
      "No shift; K changes",
      "K decreases to compensate",
    ],
    correct: 1,
    insight:
      "Adding a reactant stresses the system on the left. It relieves the stress by consuming N₂ — shift right toward products. K is unchanged.",
  },
  {
    id: "lc-2",
    category: "lechat-conc",
    difficulty: "medium",
    prompt:
      "For 2 SO₂ + O₂ ⇌ 2 SO₃, removing SO₃ as it forms will:",
    formula: "2 SO₂(g) + O₂(g) ⇌ 2 SO₃(g)",
    options: [
      "Shift left, since products are missing.",
      "Shift right, producing more SO₃.",
      "Have no effect once equilibrium is established.",
      "Decrease K.",
    ],
    correct: 1,
    insight:
      "Removing a product lowers Q below K. The forward reaction speeds up to replace it — this is how industry drives reactions to completion.",
  },
  {
    id: "lc-3",
    category: "lechat-conc",
    difficulty: "hard",
    prompt:
      "For Fe³⁺(aq) + SCN⁻(aq) ⇌ FeSCN²⁺(aq) (deep red), adding AgNO₃ removes SCN⁻ as AgSCN(s). The solution will:",
    formula: "Fe³⁺ + SCN⁻ ⇌ FeSCN²⁺  (deep red)",
    options: [
      "Become more red (shift right).",
      "Become less red (shift left).",
      "Stay the same color.",
      "Turn blue.",
    ],
    correct: 1,
    insight:
      "Removing a reactant (SCN⁻) shifts equilibrium LEFT, breaking down red FeSCN²⁺. The color fades.",
  },

  // ── Le Chât: pressure / volume ─────────────────────────────────────────
  {
    id: "lp-1",
    category: "lechat-pressure",
    difficulty: "easy",
    prompt:
      "For N₂(g) + 3 H₂(g) ⇌ 2 NH₃(g), INCREASING the pressure (decreasing volume) shifts equilibrium toward:",
    formula: "4 mol gas ⇌ 2 mol gas",
    options: [
      "The side with MORE moles of gas (left).",
      "The side with FEWER moles of gas (right).",
      "No shift — pressure never matters.",
      "Both sides equally.",
    ],
    correct: 1,
    insight:
      "Squeezing the system favors the side with fewer gas molecules to relieve pressure. Here: 2 mol < 4 mol, so shift right toward NH₃.",
  },
  {
    id: "lp-2",
    category: "lechat-pressure",
    difficulty: "medium",
    prompt:
      "For H₂(g) + I₂(g) ⇌ 2 HI(g), what is the effect of compressing the container?",
    formula: "2 mol gas ⇌ 2 mol gas",
    options: [
      "Shifts right",
      "Shifts left",
      "No shift (equal moles of gas on both sides)",
      "K doubles",
    ],
    correct: 2,
    insight:
      "When Δn(gas) = 0, changing total pressure compresses both sides equally — no shift in position.",
  },
  {
    id: "lp-3",
    category: "lechat-pressure",
    difficulty: "hard",
    prompt:
      "Adding an INERT gas (e.g., Ar) at constant volume to N₂ + 3H₂ ⇌ 2NH₃:",
    options: [
      "Shifts right (more total pressure).",
      "Shifts left.",
      "Causes no shift — partial pressures of N₂, H₂, NH₃ are unchanged.",
      "Doubles K.",
    ],
    correct: 2,
    insight:
      "At constant V, adding an inert gas raises total P but the partial pressures of the reactive gases don't change. Q stays equal to K — no shift.",
  },

  // ── Le Chât: temperature ───────────────────────────────────────────────
  {
    id: "lt-1",
    category: "lechat-temp",
    difficulty: "easy",
    prompt:
      "For an EXOTHERMIC reaction (A + B ⇌ C + heat), INCREASING temperature shifts equilibrium:",
    formula: "A + B ⇌ C + heat   (ΔH < 0)",
    options: [
      "Right — produces more C.",
      "Left — heat is a 'product' being added; shift away from it.",
      "No shift; only K changes.",
      "Right, and K increases.",
    ],
    correct: 1,
    insight:
      "Treat heat as a product for exo. Adding heat shifts the system LEFT, and K DECREASES with rising T.",
  },
  {
    id: "lt-2",
    category: "lechat-temp",
    difficulty: "medium",
    prompt:
      "For an ENDOTHERMIC reaction (heat + A ⇌ B), how does K change as T increases?",
    formula: "heat + A ⇌ B   (ΔH > 0)",
    options: [
      "K decreases",
      "K increases",
      "K is unchanged (only position shifts)",
      "K becomes negative",
    ],
    correct: 1,
    insight:
      "Endo: heat is a 'reactant'. Adding heat shifts right, producing more B — so K (= [B]/[A]) increases.",
  },
  {
    id: "lt-3",
    category: "lechat-temp",
    difficulty: "hard",
    prompt:
      "2 NO₂(g, brown) ⇌ N₂O₄(g, colorless), ΔH = −58 kJ. A sealed tube is plunged into ice water. The gas will appear:",
    formula: "2 NO₂(brown) ⇌ N₂O₄(colorless)   ΔH = −58 kJ",
    options: [
      "Darker brown (shift left).",
      "Lighter / colorless (shift right).",
      "Unchanged in color.",
      "Black.",
    ],
    correct: 1,
    insight:
      "Exothermic forward → cooling removes 'heat product' → shifts RIGHT toward colorless N₂O₄. The classic NO₂/N₂O₄ demo.",
  },

  // ── catalyst / other ───────────────────────────────────────────────────
  {
    id: "lo-1",
    category: "lechat-other",
    difficulty: "easy",
    prompt: "What does adding a CATALYST do to a system at equilibrium?",
    options: [
      "Shifts equilibrium toward products.",
      "Shifts equilibrium toward reactants.",
      "Speeds up forward and reverse equally — no shift, K unchanged.",
      "Increases K but not the rate.",
    ],
    correct: 2,
    insight:
      "Catalysts lower Ea for both directions equally. Equilibrium is reached FASTER, but the position and K are unchanged.",
  },
  {
    id: "lo-2",
    category: "lechat-other",
    difficulty: "medium",
    prompt:
      "Which change will alter the value of K for a given reaction?",
    options: [
      "Adding more reactant",
      "Changing temperature",
      "Adding a catalyst",
      "Changing the volume of the container",
    ],
    correct: 1,
    insight:
      "Only temperature changes K. Concentration, pressure/volume, and catalysts can shift the position but K is fixed at constant T.",
  },
  {
    id: "lo-3",
    category: "lechat-other",
    difficulty: "hard",
    prompt:
      "For the Haber process N₂ + 3H₂ ⇌ 2NH₃ (ΔH < 0), industry uses HIGH pressure and only MODERATELY high T (~450 °C). Why not very high T?",
    options: [
      "High T destroys the catalyst's activity completely.",
      "Exothermic forward → very high T shifts equilibrium LEFT, lowering NH₃ yield, even though rate is faster.",
      "Very high T raises K too much, making the reaction explosive.",
      "Nitrogen evaporates above 450 °C.",
    ],
    correct: 1,
    insight:
      "Trade-off between rate (favored by high T) and yield (hurt by high T for exo). 450 °C with an iron catalyst is the practical sweet spot.",
  },
];